import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, MapPin, UtensilsCrossed } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useTable } from '../../context/TableContext';
import { useAuth } from '../../context/AuthContext';

const CustomerTableSelection: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { tables } = useData();
  const { selectedTable, selectTable } = useTable();
  const { refreshCustomerSession } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState<string>('All');

  const locations = ['All', ...Array.from(new Set(tables.map(t => t.location)))];
  
  const availableTables = tables.filter(t => 
    t.status === 'available' && 
    (selectedLocation === 'All' || t.location === selectedLocation)
  );

  const handleTableSelect = (tableId: string) => {
    selectTable(tableId);
    
    // Update customer session to mark login as complete
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const session = JSON.parse(customerSession);
        session.loginComplete = true;
        session.tableId = tableId;
        localStorage.setItem('bookfood_customer_session', JSON.stringify(session));
        
        // Refresh auth context to update user state
        refreshCustomerSession();
      } catch (error) {
        console.error('Error updating customer session:', error);
      }
    }
    
    setCurrentScreen('customer-home');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'occupied':
        return 'bg-red-500';
      case 'reserved':
        return 'bg-yellow-500';
      default:
        return 'bg-slate-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                Select Your Table
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Current Selection */}
        {selectedTable && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="gradient-bg rounded-2xl p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-6xl">🪑</div>
                <div>
                  <p className="text-white text-sm opacity-90 mb-1">Currently Selected</p>
                  <h3 className="text-white font-bold text-2xl">{selectedTable.tableNumber}</h3>
                  <p className="text-white text-sm opacity-90">{selectedTable.location} • {selectedTable.capacity} seats</p>
                </div>
              </div>
              <motion.button
                onClick={() => setCurrentScreen('customer-home')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-orange-600 px-6 py-3 rounded-xl font-bold text-base hover:bg-gray-100 transition-colors shadow-lg"
              >
                Continue
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Location Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-xl font-bold text-white mb-4">Filter by Location</h2>
          <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
            {locations.map((location) => (
              <motion.button
                key={location}
                onClick={() => setSelectedLocation(location)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 shadow-lg ${
                  selectedLocation === location
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                    : 'bg-slate-800 text-gray-300 hover:bg-slate-700 border border-gray-700'
                }`}
              >
                {location}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Available Tables */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-xl font-bold text-white mb-4">
            Available Tables ({availableTables.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {availableTables.map((table, index) => (
              <motion.div
                key={table.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleTableSelect(table.id)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`food-card cursor-pointer transition-all ${
                  selectedTable?.id === table.id
                    ? 'border-orange-500 shadow-xl shadow-orange-500/20 bg-gradient-to-br from-slate-800 to-slate-700'
                    : 'hover:border-orange-400 hover:shadow-lg'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-3xl">🪑</span>
                  </div>
                  <div className={`w-4 h-4 rounded-full ${getStatusColor(table.status)} shadow-lg`} />
                </div>
                <h3 className="text-white font-bold text-xl mb-2">{table.tableNumber}</h3>
                <div className="flex items-center text-gray-400 text-sm mb-2">
                  <MapPin size={16} className="mr-2 text-orange-400" />
                  <span>{table.location}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <Users size={16} className="mr-2 text-orange-400" />
                  <span>{table.capacity} seats</span>
                </div>
                {selectedTable?.id === table.id && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 pt-3 border-t border-gray-600"
                  >
                    <span className="text-orange-400 text-sm font-semibold">✓ Selected</span>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {availableTables.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="food-card text-center py-12"
            >
              <div className="text-7xl mb-4">🪑</div>
              <h3 className="text-2xl font-bold text-white mb-2">No Tables Available</h3>
              <p className="text-gray-400 text-lg">Please try a different location or check back later</p>
            </motion.div>
          )}
        </motion.div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="food-card"
        >
          <h3 className="text-xl font-bold text-white mb-4">Status Legend</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center bg-slate-700 rounded-xl p-4">
              <div className="w-4 h-4 rounded-full bg-green-500 mr-3 shadow-lg" />
              <span className="text-gray-300 font-medium">Available</span>
            </div>
            <div className="flex items-center bg-slate-700 rounded-xl p-4">
              <div className="w-4 h-4 rounded-full bg-red-500 mr-3 shadow-lg" />
              <span className="text-gray-300 font-medium">Occupied</span>
            </div>
            <div className="flex items-center bg-slate-700 rounded-xl p-4">
              <div className="w-4 h-4 rounded-full bg-yellow-500 mr-3 shadow-lg" />
              <span className="text-gray-300 font-medium">Reserved</span>
            </div>
          </div>
        </motion.div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerTableSelection;
